import React, { useCallback, useEffect, useMemo } from 'react';
import { Drawer } from '@mui/material';
import { FormProvider, useForm } from 'react-hook-form';

import {
  PaymentUsersFiltersFormType,
  PaymentUsersFiltersProps,
} from './payment-users-filters.types';
import { FilterWrapper } from '../../../../../../shared/components/filters/filter-wrapper';
import { FilterWorkersSelect } from '../../../../../../shared/components/filters/inputs/flex-workers/filter-workers-all-select';
import { FilterIban } from '../../../../../../shared/components/filters/inputs/iban/filter-iban';
import { FilterPaymentUserState } from '../../../../../../shared/components/filters/inputs/payment-user-state/payment-user-state';
import {
  FlexWorkerSelectStateProps,
  getWorkerLabel,
} from '../../../flexWorkers/flex-worker-select';

export const PaymentUsersFilters = ({
  toggleFilters,
  handleFiltersChange,
  appliedFilters,
  isOpen,
  filterData,
  flexWorkerOption,
  flexWorkerSearch,
  setFlexWorkerSearch,
  setFlexWorkerOption,
  selectedWorkerData,
}: PaymentUsersFiltersProps & FlexWorkerSelectStateProps) => {
  useEffect(() => {
    if (appliedFilters.worker && selectedWorkerData) {
      const workerLabel = getWorkerLabel(selectedWorkerData);
      setFlexWorkerOption({ label: workerLabel, value: appliedFilters.worker });
    }
  }, []);

  const defaultFilterValues = useMemo(() => {
    return {};
  }, [appliedFilters, filterData]);

  const form = useForm<PaymentUsersFiltersFormType>({ defaultValues: { ...defaultFilterValues } });

  useEffect(() => {
    form.setValue('worker', flexWorkerOption);
  }, [flexWorkerOption]);

  const handleClear = useCallback(() => {
    form.reset({
      iban: undefined,
      allowIbanFilter: false,
      worker: undefined,
      allowWorkersFilter: false,
      state: undefined,
      allowStateFilter: false,
    });
  }, [defaultFilterValues, form]);

  const handleCancel = () => {
    toggleFilters();
  };

  const handleSubmit: React.FormEventHandler<HTMLFormElement> = useCallback(
    (e) => {
      e.preventDefault();

      form.handleSubmit((data) => {
        const dataToSubmit = {
          iban: data.allowIbanFilter && data.iban ? data.iban : undefined,
          state: data.allowStateFilter && data.state ? data.state : undefined,
          worker: data.allowWorkersFilter && data.worker ? data.worker?.value : undefined,
        };

        handleFiltersChange(dataToSubmit);
        toggleFilters();
      })();
    },
    [form, handleFiltersChange, toggleFilters]
  );

  return (
    <Drawer anchor="right" open={isOpen} onClose={handleCancel}>
      <FormProvider {...form}>
        <FilterWrapper
          filterName="Filter"
          onCancel={handleCancel}
          onClear={handleClear}
          onSubmit={handleSubmit}
        >
          <FilterWorkersSelect
            workers={filterData.workers}
            flexWorkerOption={flexWorkerOption}
            flexWorkerSearch={flexWorkerSearch}
            setFlexWorkerSearch={setFlexWorkerSearch}
            setFlexWorkerOption={setFlexWorkerOption}
          />
          <FilterIban />
          <FilterPaymentUserState />
        </FilterWrapper>
      </FormProvider>
    </Drawer>
  );
};
